import moment from 'moment';
import {LeanDocument, Types} from 'mongoose';
import {MessengerTypesEnum} from '@/enums/messenger/MessengerTypes.enum';
import {Conversation, ConversationMessage, ConversationProfileTypes} from '@/classes/dto/messenger/response/messages/GetConversations.response.dto';
import {MessengerMailDocument, MessengerWhatsAppMessageDocument} from '@/documentTypes/messenger/messages';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {PlacetelCallLogEntryDocument} from '@/documentTypes/recorder/discriminator/PlacetelCallLogEntry.discriminator';
import {isPopulated, retrieveObjectId} from '@/helpers/isObjectId.helper';

export type MailMessage = LeanDocument<MessengerMailDocument>;
export type WhatsAppMessage = LeanDocument<MessengerWhatsAppMessageDocument>;
export type CallEntry = LeanDocument<PlacetelCallLogEntryDocument>;

/** One row of the chat: a message, a call or a day separator */
export type ChatRow =
    { kind: 'day'; key: string; label: string }
    | { kind: 'message'; key: string; message: ConversationMessage; own: boolean; at: Date }
    | { kind: 'call'; key: string; call: CallEntry; at: Date };

export const isMail = (message: ConversationMessage): message is MailMessage => message.type === MessengerTypesEnum.MAIL;

export const isWhatsApp = (message: ConversationMessage): message is WhatsAppMessage => message.type === MessengerTypesEnum.WHATS_APP;

/** Own messages were sent from one of our mailboxes / numbers */
/** Digits only: the own number is stored as "+15550644544", sent messages carry "+1 555 064 4544" */
const numberDigits = (value: string | undefined | null): string => (value || '').replace(/\D/g, '');

export const isOwnMessage = (message: ConversationMessage): boolean => {
    if (isMail(message)) {
        return message.username === message.fromMail;
    }
    const {phoneNumber, fromNumber} = message as WhatsAppMessage;
    return !!numberDigits(phoneNumber) && numberDigits(phoneNumber) === numberDigits(fromNumber);
};

export const messageSentAt = (message: ConversationMessage): Date => new Date(message.sentAt);

export const dayLabel = (date: Date): string => {
    const m = moment(date);
    if (m.isSame(moment(), 'day')) {
        return 'Heute';
    }
    if (m.isSame(moment().subtract(1, 'day'), 'day')) {
        return 'Gestern';
    }
    return m.locale('de').format('dddd, D. MMMM YYYY');
};

export const timeLabel = (date: Date | string): string => moment(date).format('HH:mm');

/** Short time for the list: time today, weekday this week, else date */
export const listTimeLabel = (date: Date | string): string => {
    const m = moment(date).locale('de');
    if (m.isSame(moment(), 'day')) {
        return m.format('HH:mm');
    }
    if (m.isAfter(moment().subtract(6, 'days'))) {
        return m.format('dd.');
    }
    return m.format('DD.MM.YY');
};

/** Merges loaded messages and calls into one list with day separators, oldest first */
export const buildChatRows = (messages: Array<Types.ObjectId | ConversationMessage>, calls: CallEntry[]): ChatRow[] => {
    const rows: ChatRow[] = [];
    for (const message of messages) {
        if (isPopulated(message)) {
            rows.push({kind: 'message', key: `m:${message._id}`, message, own: isOwnMessage(message), at: messageSentAt(message)});
        }
    }
    for (const call of calls) {
        rows.push({kind: 'call', key: `c:${call._id}`, call, at: new Date(call.sortingDate || call.createdAt)});
    }
    rows.sort((a, b) => (a as { at: Date }).at.getTime() - (b as { at: Date }).at.getTime());
    const withDays: ChatRow[] = [];
    let lastDay = '';
    for (const row of rows) {
        const day = moment((row as { at: Date }).at).format('YYYY-MM-DD');
        if (day !== lastDay) {
            withDays.push({kind: 'day', key: `d:${day}`, label: dayLabel((row as { at: Date }).at)});
            lastDay = day;
        }
        withDays.push(row);
    }
    return withDays;
};

export const profileLink = (conversation: Pick<Conversation, 'profileId' | 'profileType'>): string | undefined => {
    if (!conversation.profileId || !conversation.profileType) {
        return undefined;
    }
    const paths: Record<ConversationProfileTypes, string> = {
        [CrudEntityTypes.EMPLOYEE]: '/employees',
        [CrudEntityTypes.INSTITUTION]: '/institutions',
        [CrudEntityTypes.PARENT]: '/parents',
        [CrudEntityTypes.STUDENT]: '/students',
        [CrudEntityTypes.RESPONSIBLE]: '/responsibles',
    };
    return `${paths[conversation.profileType]}/${conversation.profileId}`;
};

export const profileTypeLabel = (type?: ConversationProfileTypes): string => {
    switch (type) {
        case CrudEntityTypes.EMPLOYEE:
            return 'Mitarbeiter:in';
        case CrudEntityTypes.INSTITUTION:
            return 'Standort';
        case CrudEntityTypes.PARENT:
            return 'Eltern';
        case CrudEntityTypes.STUDENT:
            return 'Schüler:in';
        case CrudEntityTypes.RESPONSIBLE:
            return 'Ansprechpartner:in';
        default:
            return 'Keinem Profil zugeordnet';
    }
};

/** Last incoming WhatsApp message decides the 24h free-text window */
export const whatsAppWindowEnd = (messages: Array<Types.ObjectId | ConversationMessage>): Date | undefined => {
    let latest: Date | undefined;
    for (const message of messages) {
        if (!isPopulated(message) || !isWhatsApp(message) || isOwnMessage(message) || (message.whatsAppErrors || []).length > 0) {
            continue;
        }
        const at = new Date(message.deliveredAt || message.sentAt);
        if (!latest || at > latest) {
            latest = at;
        }
    }
    return latest ? moment(latest).add(24, 'hours').toDate() : undefined;
};

/** Header image of a sent WhatsApp template ("Bild-Kopfzeile"): only http(s) links, else undefined (mails, old messages) */
export const headerImageOf = (message: ConversationMessage): string | undefined => {
    if (!isWhatsApp(message)) {
        return undefined;
    }
    const url = (message.headerImageUrl || '').trim();
    return /^https?:\/\//i.test(url) ? url : undefined;
};

export const stripHtml = (html?: string): string => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Drafts live per conversation key and channel in localStorage (per browser convenience, never shared) */
export type Draft = { subject?: string; message: string; repliesTo?: string };

export const draftKey = (conversationKey: string, type: MessengerTypesEnum) => `messenger.draft.${type}.${conversationKey}`;

export const loadDraft = (conversationKey: string, type: MessengerTypesEnum): Draft | undefined => {
    try {
        const raw = localStorage.getItem(draftKey(conversationKey, type));
        return raw ? JSON.parse(raw) as Draft : undefined;
    } catch {
        return undefined;
    }
};

export const saveDraft = (conversationKey: string, type: MessengerTypesEnum, draft: Draft | undefined) => {
    try {
        if (!draft || (!draft.message && !draft.subject)) {
            localStorage.removeItem(draftKey(conversationKey, type));
        } else {
            localStorage.setItem(draftKey(conversationKey, type), JSON.stringify(draft));
        }
    } catch {
        // storage unavailable (private mode); drafts are a convenience only
    }
};

export const initialsOf = (name: string): string => {
    const parts = name.replace(/[^\p{L}\p{N}\s]/gu, '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
        return '?';
    }
    return parts.length === 1 ? parts[0].slice(0, 2).toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Fresh conversations of the list with the messages already loaded in the previous ones (same id) */
export function keepLoadedMessages(fresh: Conversation[], previous: Conversation[]): Conversation[] {
    const loaded = new Map<string, ConversationMessage>();
    for (const conversation of previous) {
        for (const message of conversation.messages) {
            if (isPopulated(message)) {
                loaded.set(String((message as ConversationMessage)._id), message as ConversationMessage);
            }
        }
    }
    if (!loaded.size) {
        return fresh;
    }
    return fresh.map((conversation) => ({
        ...conversation,
        messages: conversation.messages.map((message) => (isPopulated(message) ? message : loaded.get(String(retrieveObjectId(message))) || message)),
    }));
}
